import React, { Children, memo } from 'react';
import { motion } from 'framer-motion';
import { BookOpenIcon, CompassIcon, PaletteIcon, CheckCircle2Icon, MonitorIcon, HandHeartIcon, ArrowRightIcon } from 'lucide-react';
import { Container } from '../../components/ui/Container';
import { Button } from '../../components/ui/Button';
import { SubpageHero } from '../../components/sections/subpages/SubpageHero';
import { PatternDecor } from '../../components/ui/PatternDecor';
import { Link } from 'react-router-dom';
const ageGroups = [{
  emoji: '🌱',
  title: 'Age 5–7',
  program: 'Foundations of Identity & Belonging',
  focus: 'Helping young children feel safe, confident, and proud of who they are.',
  columns: [{
    title: 'Core Learning',
    items: ['Self-awareness and emotions', 'Kindness, respect, and empathy', 'Confidence in expressing identity']
  }, {
    title: 'Islamic Faith & Practice',
    items: ['Islamic values & Quran stories: learning faith-based principles through engaging prophetic narratives.', "Quran Literacy & Arabic: foundational Arabic alphabet, letter recognition, and proper sounds.", 'Salah & Manners (Adab): introduction to daily prayer steps, simple actions, and Islamic etiquette.']
  }, {
    title: 'Enrichment & Hobbies',
    items: ['Creative Arabic calligraphy', 'Art, stories, and creative play', 'Nasheed and rhythm']
  }]
}, {
  emoji: '🌿',
  title: 'Age 8–10',
  program: 'Identity & Confidence',
  focus: 'Supporting children as they become more socially aware and independent.',
  columns: [{
    title: 'Core Learning',
    items: ['Identity and self-respect', 'Communication and friendships', 'Navigating differences']
  }, {
    title: 'Islamic Faith & Practice',
    items: ['Establishing Salah: practical guidance and consistency in performing the five daily prayers.', "Quran & Arabic Reading: mastering Tajweed basics, pronunciation, and fluent reading from the Mushaf.", 'Foundations of Aqidah (Faith): understanding the Pillars of Iman and the attributes of Allah.']
  }, {
    title: 'Enrichment & Hobbies',
    items: ['Arabic calligraphy', 'European language tracks', 'Creative writing and storytelling']
  }]
}, {
  emoji: '🌳',
  title: 'Age 11–14',
  program: 'Strength, Responsibility & Belonging',
  focus: 'Preparing pre-teens and teens for real-life social and identity challenges.',
  columns: [{
    title: 'Core Learning',
    items: ['Identity in non-Muslim society', 'Boundaries and self-respect', 'Critical thinking and ethics']
  }, {
    title: 'Islamic Faith & Practice',
    items: ['Fiqh of Worship (Ibadah): practical rules of purification (Taharah) and prayer for transitioning into adulthood.', "Quran Recitation & Meaning: improving recitation flow, memorization, and understanding Quranic ethics.", 'Aqidah & Islamic History: strengthening core creed and studying key milestones of Islamic civilization.']
  }, {
    title: 'Enrichment & Hobbies',
    items: ['Advanced calligraphy', 'Public speaking', 'Creative media (writing, video, projects)']
  }]
}, {
  emoji: '🌍',
  title: 'Age 15–18',
  program: 'Leadership, Identity & Life Skills',
  focus: 'Supporting teenagers as they transition into adulthood with confidence and responsibility.',
  columns: [{
    title: 'Core Learning',
    items: ['Identity, purpose, and leadership', 'Ethical decision-making', 'Representation and responsibility']
  }, {
    title: 'Islamic Faith & Practice',
    items: ['Applied Fiqh & Worldview: understanding Halal and Haram in daily transactions, modern ethics, and lifestyle.', 'Defending the Faith: addressing contemporary doubts and developing a robust Islamic intellectual identity.', "Quran & Spiritual Connection: maintaining a daily relationship with the Quran through recitation and reflection."]
  }, {
    title: 'Enrichment & Hobbies',
    items: ['Advanced language tracks', 'Debate and dialogue', 'Cultural and research projects']
  }]
}];
const pillars = [{
  icon: CompassIcon,
  title: 'Core Learning',
  bg: 'bg-sage-100',
  points: ['Identity & confidence', 'Social skills & ethics', 'Integration without isolation', 'Communication & leadership tools'],
  body: 'The foundation: who children are, how they relate to others, and how they carry themselves with calm confidence.'
}, {
  icon: BookOpenIcon,
  title: 'Islamic Faith & Cultural Identity',
  bg: 'bg-[#F7EDE9]',
  points: ['Islamic faith and practice in daily life', 'Arabic basics', 'Quran: recitation basics & meanings', 'Cultural identity and community heritage'],
  body: 'An age-appropriate foundation in Islamic faith (Deen) and practice, alongside a strong connection to cultural heritage — nurtured with wisdom and gentleness.'
}, {
  icon: PaletteIcon,
  title: 'Enrichment & Hobbies',
  bg: 'bg-sand-100',
  points: ['Languages', 'Calligraphy', 'Creative classes', 'Projects'],
  body: 'Skills and creativity that build confidence, expression, and curiosity beyond the core curriculum.'
}];
export function KidsPrograms() {
  return <>
      <SubpageHero eyebrow="Education that builds identity, confidence, and belonging" title="Kids Programs" paragraphs={["Our children's programs combine three essential elements: identity and life skills, Islamic and cultural foundations, and languages and creative enrichment."]} primaryCta={{
      label: 'View Programs',
      to: '#age-groups'
    }} secondaryCta={{
      label: 'Scholarships & Support',
      to: '/en/children/scholarships'
    }} image="https://cdn.magicpatterns.com/uploads/ppTjcSFE6RXTZ38TUFMDbN/Kids-2.jpg" imageAlt="Group of Muslim children smiling as they read an illustrated picture book together outdoors" compact masked data-id="element-3254" data-mp="2t02v3qyu6rt5" />

      {/* Structure */}
      <section className="relative py-16 md:py-24 overflow-hidden isolate bg-sage-50/60" data-id="element-3255" data-mp="26zvm52182mnw">
        <PatternDecor side="right" y="top" size="lg" data-id="element-3256" data-mp="3ndvlobw5oaam" />
        <Container data-id="element-3257" data-mp="3igvefxnkl1hx">
          <h2 className="font-display text-3xl md:text-5xl text-ink-900 leading-tight max-w-3xl" data-id="element-3258" data-mp="3ifaka9563kkl">
            How our programs are structured
          </h2>
          <p className="mt-5 text-ink-700 leading-relaxed" data-id="element-3259" data-mp="197nwpctl76mi">
            This balanced approach allows Muslim children to integrate
            confidently into society while staying firmly rooted in their
            Islamic faith while confidently embracing their cultural heritage.
          </p>
          <div className="mt-10 grid md:grid-cols-3 gap-5" data-id="element-3260" data-mp="1nwjzaucvjrfq">
            {pillars.map((p, i) => <motion.div key={p.title} initial={{
            opacity: 0,
            y: 16
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-60px'
          }} transition={{
            duration: 0.5,
            delay: i * 0.08
          }} className={`rounded-2xl ${p.bg} border border-ink-900/5 p-6 shadow-soft`} data-id="element-3261" data-mp="1ry5jl0rqn7zy" data-mpi={i}>
                <span className="w-11 h-11 inline-flex items-center justify-center rounded-full bg-white border border-forest-700/10 text-forest-700" data-id="element-3262" data-mp="6t6j4gxvy1a3">
                  <p.icon size={18} strokeWidth={1.6} data-id="element-3263" data-mp="1uvag9jbtwtg" />
                </span>
                <h3 className="mt-5 font-display text-lg text-ink-900" data-id="element-3264" data-mp="3t1659nxqu1cc">
                  {p.title}
                </h3>
                <p className="mt-3 text-ink-700 text-sm leading-relaxed" data-id="element-3265" data-mp="uhg2zdbjgeyl">
                  {p.body}
                </p>
                <ul className="mt-4 space-y-2" data-id="element-3266" data-mp="pkhaez7iwg2i">
                  {p.points.map(pt => <li key={pt} className="flex items-center gap-2 text-sm text-ink-700" data-id="element-3267" data-mp="kirvh8h6ji95">
                      <CheckCircle2Icon size={14} className="text-forest-700 flex-shrink-0" strokeWidth={1.6} data-id="element-3268" data-mp="35b2dqextxs1p" />
                      {pt}
                    </li>)}
                </ul>
              </motion.div>)}
          </div>
        </Container>
      </section>

      {/* What children gain */}
      <section className="py-16 md:py-24 bg-white/60" data-id="element-3269" data-mp="1pr2t40x6iq2x">
        <Container data-id="element-3270" data-mp="1uikwevmod2r2">
          <div className="grid lg:grid-cols-12 gap-12 items-center" data-id="element-3271" data-mp="1uiulmgzwb5vp">
            <div className="lg:col-span-6" data-id="element-3272" data-mp="1zho12eyqt6yi">
              <h2 className="font-display text-3xl md:text-4xl text-ink-900 leading-tight" data-id="element-3273" data-mp="1zgmf3fg6v020">
                What children gain
              </h2>
              <ul className="mt-6 space-y-4 text-ink-700" data-id="element-3274" data-mp="24iliuqfubyzh">
                {['Calm confidence in who they are', 'Strong, age-appropriate Islamic foundation', 'Communication and social skills for diverse environments', 'Resilience in the face of pressure or misunderstanding', 'Creative outlets that build self-expression', 'A pathway to lifelong success through confident integration'].map(p => <li key={p} className="flex items-start gap-3" data-id="element-3275" data-mp="33iyc91x2ufmq">
                    <CheckCircle2Icon size={18} className="text-forest-700 mt-1 flex-shrink-0" strokeWidth={1.6} data-id="element-3276" data-mp="33lb2e2cznwav" />
                    <span data-id="element-3277" data-mp="3dfb55xksspgf">{p}</span>
                  </li>)}
              </ul>
            </div>
            <div className="lg:col-span-6" data-id="element-3278" data-mp="3mic8jqia42ge">
              <div className="aspect-[4/3] rounded-3xl overflow-hidden shadow-card" data-id="element-3279" data-mp="3mh15xu0kl0qv">
                <img src="https://cdn.magicpatterns.com/uploads/h3APRV42sKgSqaH4rUg3ZZ/Children-Gain.jpg" alt="Three Muslim students collaborating around a tablet and study materials" className="w-full h-full object-cover" data-id="element-3280" data-mp="3ra3q5bjgixue" />
              </div>
            </div>
          </div>
        </Container>
      </section>

      {/* Age-based pathways */}
      <section id="age-groups" className="py-16 md:py-24 scroll-mt-24 bg-[#F7EDE9]/60" data-id="element-3281" data-mp="1tm9miw3gsr4z">
        <Container data-id="element-3282" data-mp="1pibqwvdclygo">
          <h2 className="font-display text-3xl md:text-5xl text-ink-900 leading-tight" data-id="element-3283" data-mp="1pm9afi3u8s13">
            Age-based pathways
          </h2>
          <div className="mt-5 w-full space-y-3 text-ink-700 leading-relaxed" data-id="element-3284" data-mp="1fm8m1hr43lq1">
            <p data-id="element-3285" data-mp="2d9hczs9vtmzf">
              Each age group follows a structured curriculum adapted to
              children's emotional and developmental needs.
            </p>
            <p data-id="element-3286" data-mp="28b653n35dmju">
              <strong className="text-ink-900" data-id="element-3287" data-mp="28ch65f57kg79">Note on Group Formats:</strong>{' '}
              Our early childhood tracks (Ages 5–10) welcome mixed cohorts to
              encourage peer learning. For our pre-teen and teenage programs
              (Ages 11–14 and 15–18), classes are conducted in dedicated
              gender-specific environments to respect privacy and Islamic
              guidelines for older children.
            </p>
          </div>

          <div className="mt-12 space-y-6" data-id="element-3288" data-mp="pczs9expmw3w">
            {ageGroups.map((g, i) => {
            const tones = ['bg-sage-100 border-forest-700/10', 'bg-[#EDF2F7] border-[#C7D4E0]/40', 'bg-[#F7EDE9] border-[#E0BFB3]/40', 'bg-sand-100 border-sand-300/40'];
            return <motion.div key={g.title} initial={{
              opacity: 0,
              y: 16
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true,
              margin: '-80px'
            }} transition={{
              duration: 0.5,
              delay: i * 0.06
            }} className={`rounded-2xl border shadow-soft p-6 md:p-10 ${tones[i % tones.length]}`} data-id="element-3289" data-mp="5nz2xpmx8kc0" data-mpi={i}>
                  <div className="flex items-center gap-3" data-id="element-3290" data-mp="264lgbze0sb24">
                    <span className="text-2xl" aria-hidden="true" data-id="element-3291" data-mp="215im79zkvp8x">
                      {g.emoji}
                    </span>
                    <h3 className="font-display text-xl md:text-2xl tracking-wide uppercase text-ink-900" data-id="element-3292" data-mp="2ex6z3fhnihqy">
                      {g.title}
                    </h3>
                  </div>

                  <div className="mt-6 space-y-2 text-ink-700" data-id="element-3293" data-mp="2otic67p2fbmg">
                    <p data-id="element-3294" data-mp="2jvgtvozgrgc1">
                      <span className="font-semibold text-ink-900" data-id="element-3295" data-mp="2jw9su3s6l1jm">
                        Program:
                      </span>{' '}
                      {g.program}
                    </p>
                    <p data-id="element-3296" data-mp="2tt4s4owa35yy">
                      <span className="font-semibold text-ink-900" data-id="element-3297" data-mp="2tuzd237juy31">
                        Program focus:
                      </span>{' '}
                      {g.focus}
                    </p>
                  </div>

                  <div className="mt-8 grid md:grid-cols-3 gap-8" data-id="element-3298" data-mp="zhg7q77a2b76">
                    {g.columns.map(col => <div key={col.title} data-id="element-3299" data-mp="10z53x0ouys2t">
                        <p className="text-[11px] font-semibold tracking-[0.14em] uppercase text-ink-900 mb-4" data-id="element-3300" data-mp="15v3xr3syh2am">
                          {col.title}
                        </p>
                        <ul className="space-y-2.5 text-ink-700 text-sm" data-id="element-3301" data-mp="1ewbisk6go6mr">
                          {col.items.map(it => <li key={it} className="flex items-start gap-2.5" data-id="element-3302" data-mp="i7c8z0q8awsg">
                              <CheckCircle2Icon size={14} className="text-forest-700 mt-1 flex-shrink-0" strokeWidth={1.8} data-id="element-3303" data-mp="m7efbn6wsee2" />
                              <span data-id="element-3304" data-mp="vz0mn8g4ltbj">{it}</span>
                            </li>)}
                        </ul>
                      </div>)}
                  </div>
                </motion.div>;
          })}
          </div>
        </Container>
      </section>

      {/* Next steps */}
      <section className="relative py-16 md:py-24 overflow-hidden isolate bg-white/60" data-id="element-3305" data-mp="3n4ggcyrxbtat">
        <PatternDecor side="left" y="middle" size="md" data-id="element-3306" data-mp="3i53rk8hhsc5l" />
        <Container data-id="element-3307" data-mp="3i3iyyoeqn3ad">
          <h2 className="font-display text-3xl md:text-5xl text-ink-900 leading-tight" data-id="element-3308" data-mp="3i76rplcczlo1">
            Next steps
          </h2>
          <p className="mt-5 text-ink-700 max-w-3xl leading-relaxed" data-id="element-3309" data-mp="3945n5p3562mh">
            Everything else is explained on dedicated pages — so you can decide
            quickly and confidently.
          </p>
          <div className="mt-10 grid md:grid-cols-2 gap-5" data-id="element-3310" data-mp="2zecm3i0ml5wh">
            <motion.div initial={{
            opacity: 0,
            y: 16
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-60px'
          }} transition={{
            duration: 0.5
          }} className="rounded-2xl bg-[#EDF2F7] border border-[#C7D4E0]/50 p-7 md:p-8 shadow-soft" data-id="element-3311" data-mp="2zevti4sngbw9">
              <span className="w-12 h-12 inline-flex items-center justify-center rounded-xl bg-white text-forest-700" data-id="element-3312" data-mp="2fqpx0ij1awek">
                <MonitorIcon size={20} strokeWidth={1.6} data-id="element-3313" data-mp="26s4euohh5jeb" />
              </span>
              <h3 className="mt-5 font-display text-2xl text-ink-900" data-id="element-3314" data-mp="26u8mnhihv2jv">
                Online Learning
              </h3>
              <p className="mt-3 text-ink-700 leading-relaxed" data-id="element-3315" data-mp="2zgjvffg989rr">
                How lessons work, safety standards, group size, and parent
                visibility.
              </p>
              <Link to="/en/children/online-learning" className="mt-5 inline-flex items-center gap-1.5 text-forest-700 font-medium hover:gap-2 transition-all" data-id="element-3316" data-mp="2kq2s1qk279s8">
                View Online Learning{' '}
                <ArrowRightIcon size={16} strokeWidth={1.6} data-id="element-3317" data-mp="3i8clb1011ev9" />
              </Link>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 16
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-60px'
          }} transition={{
            duration: 0.5,
            delay: 0.08
          }} className="rounded-2xl bg-sand-100 border border-sand-300/50 p-7 md:p-8 shadow-soft" data-id="element-3318" data-mp="16r1036aws5ig">
              <span className="w-12 h-12 inline-flex items-center justify-center rounded-xl bg-white text-forest-700" data-id="element-3319" data-mp="iwpwbf82she0">
                <HandHeartIcon size={20} strokeWidth={1.6} data-id="element-3320" data-mp="rvbaky6ila8z" />
              </span>
              <h3 className="mt-5 font-display text-2xl text-ink-900" data-id="element-3321" data-mp="rxebid97gnsh">
                Scholarships
              </h3>
              <p className="mt-3 text-ink-700 leading-relaxed" data-id="element-3322" data-mp="23agqj3vnp5aj">
                Eligibility, process, transparency, and how donor-supported
                seats are allocated.
              </p>
              <Link to="/en/children/scholarships" className="mt-5 inline-flex items-center gap-1.5 text-forest-700 font-medium hover:gap-2 transition-all" data-id="element-3323" data-mp="2sb63ot9gupo4">
                View Scholarships <ArrowRightIcon size={16} strokeWidth={1.6} data-id="element-3324" data-mp="313zk4u0nfv3k" />
              </Link>
            </motion.div>
          </div>
        </Container>
      </section>

      {/* Ready to begin */}
      <section className="py-16 md:py-24 bg-sand-50/60" data-id="element-3325" data-mp="3b1e24y280rxz">
        <Container data-id="element-3326" data-mp="3b409awl795fo">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8" data-id="element-3327" data-mp="3g10fr8lyoa7b">
            <div className="max-w-xl" data-id="element-3328" data-mp="3fz5vlwiikm4a">
              <h2 className="font-display text-3xl md:text-5xl text-ink-900 leading-tight" data-id="element-3329" data-mp="3k2vcs0wv01fa">
                Ready to begin?
              </h2>
              <p className="mt-5 text-ink-700 leading-relaxed" data-id="element-3330" data-mp="1lp26x7rkdgby">
                Choose a program that matches your child's age and needs — and
                start building confidence, values, and belonging in a safe
                learning space.
              </p>
            </div>
            <div className="flex flex-wrap gap-3" data-id="element-3331" data-mp="1dcs0l155sbf4">
              <Link to="#age-groups" data-id="element-3332" data-mp="18eql0q6xyu8e">
                <Button variant="primary" data-id="element-3333" data-mp="18frwuwvs7kr6">View Age-Based Programs</Button>
              </Link>
              <Link to="/en/children/scholarships" data-id="element-3334" data-mp="14ey6zxys3vvn">
                <Button variant="secondary" data-id="element-3335" data-mp="213nr7uxvp0ks">Scholarships</Button>
              </Link>
            </div>
          </div>
        </Container>
      </section>
    </>;
}